/**
 * **climax テーマの舞台の背景・マット・ゾーンの枠の画像**（Issue #565。ユーザー 2026-09-28）。
 * 手本はギルティギア Xrd（暗い鋼・真鍮・鋲・細い金と赤の線。`../THEME.md`）。
 *
 * どれも Gemini（Nano Banana Pro。`~/bin/gemini.py image`）で生成した。枠は真緑 #00FF00 の背景で作り、
 * `scripts/hud-ornament-key.py` で抜いた透過の画像（枠の中は透明）。WebP に変換して置いてある。
 * **下の `prompt` は生成したときの指示の要約**（そのまま頭に `STYLE_PROMPT`＝`../hud/ornaments.js` を付けて渡せば同じ系統の絵になる）。
 *
 * | ファイル | 何か | 寸法 |
 * |---|---|---|
 * | `stage_a.webp`〜`stage_d.webp` | 舞台の背景（16:9。**既定は B**。ほかは候補として選べる） | 1376×768 |
 * | `mat_a.webp` / `mat_b.webp` | 仮想フィールドのマット（縦長 3:4。**既定は A**。中央に横の境目、ゾーンの線は無い） | 896×1200 |
 * | `frame_monster.webp` | ゾーンの枠 B（角の括弧・赤の差し色）。**モンスター** | 796×1081 |
 * | `frame_spelltrap.webp` | 同じ形の色調調整版（青緑）。**魔法・罠 / フィールド魔法** | 同上 |
 * | `frame_other.webp` | 同じ形の色調調整版（紫灰）。**その他**（墓地・除外・デッキ・EX デッキ） | 同上 |
 * | `frame_gold.webp` | 同じ形の色調調整版（金）。候補（設定で選べる） | 同上 |
 *
 * 枠の 4 枚は**形が同じで差し色だけが違う**（色調調整版。ユーザー 2026-09-28。生成版 `frame_g_*` は使わない）。
 */
import stageA from './stage_a.webp';
import stageB from './stage_b.webp';
import stageC from './stage_c.webp';
import stageD from './stage_d.webp';
import stageWA from './stage_wa.webp';
import stageWB from './stage_wb.webp';
import stageWC from './stage_wc.webp';
import stageWD from './stage_wd.webp';
import matWA from './mat_wa.webp';
import matWB from './mat_wb.webp';
import matWC from './mat_wc.webp';
import matPlaymat from './mat_playmat.webp';
import matA from './mat_a.webp';
import matB from './mat_b.webp';
import frameMonster from './frame_monster.webp';
import frameSpelltrap from './frame_spelltrap.webp';
import frameOther from './frame_other.webp';
import frameGold from './frame_gold.webp';

/** 舞台の背景（`stageBg` の選択肢） */
export const STAGE_BACKGROUNDS = Object.freeze({
  a: Object.freeze({
    label: 'A 機械の大聖堂の回廊',
    src: stageA,
    prompt: 'Wide 16:9 fighting game stage background: a long dark gothic-mechanical cathedral hall, iron pillars, brass gears, hanging red banners, receding vanishing point, empty floor in the foreground. No characters, no text.',
  }),
  b: Object.freeze({
    label: 'B 燃える空と壊れた塔',
    src: stageB,
    prompt: 'Wide 16:9 fighting game stage background: ruined iron towers silhouetted against a burning red-orange sky with stylized cel-shaded flames, a riveted steel platform with gold and crimson trim in the foreground. No characters, no text.',
  }),
  c: Object.freeze({
    label: 'C 鋼の梁と歯車（接写）',
    src: stageC,
    prompt: 'Wide 16:9 fighting game stage background: close-up tangle of diagonal riveted steel girders, brass gears and red-black hazard stripes, dark industrial machinery. No characters, no text.',
  }),
  d: Object.freeze({
    label: 'D 薔薇窓の聖堂',
    src: stageD,
    prompt: 'Wide 16:9 fighting game stage background: gothic steel cathedral interior with a huge red stained-glass rose window and altar at the back, brass gears on the arches, iron grated floor. No characters, no text.',
  }),
  wa: Object.freeze({
    label: 'W-A 白い大理石と鋼の塔',
    src: stageWA,
    prompt: 'Guilty Gear Xrd style 16:9 stage background, OVERALL VERY LIGHT and low-contrast, little detail in the middle so a dark field stands out; Bright white marble and pale steel towers under a white sky, thin gold accents, very airy.',
  }),
  wb: Object.freeze({
    label: 'W-B 白い空と遠くの鉄骨',
    src: stageWB,
    prompt: 'Guilty Gear Xrd style 16:9 stage background, OVERALL VERY LIGHT and low-contrast, little detail in the middle so a dark field stands out; A bright overcast white sky with a faint distant steel girder city silhouette in pale grey at the bottom edge.',
  }),
  wc: Object.freeze({
    label: 'W-C 生成りの紙と墨の歯車',
    src: stageWC,
    prompt: 'Guilty Gear Xrd style 16:9 stage background, OVERALL VERY LIGHT and low-contrast, little detail in the middle so a dark field stands out; Off-white parchment paper with thin black ink line drawings of gears and gothic arches along the edges, like a manga background sketch, with a few small crimson ink accents.',
  }),
  wd: Object.freeze({
    label: 'W-D 淡い銀の斜めの板',
    src: stageWD,
    prompt: 'Guilty Gear Xrd style 16:9 stage background, OVERALL VERY LIGHT and low-contrast, little detail in the middle so a dark field stands out; Large pale silver diagonal steel plates and thin gold trims, almost white, like a bright Guilty Gear versus screen, subtle crimson accent stripes at the far edges.',
  }),
});

/** マット（`matImage` の選択肢）。`bandY` は絵の中央の境目の高さ（絵の高さに対する比。測った値） */
export const MATS = Object.freeze({
  a: Object.freeze({
    label: 'A 刻印の床板と歯車',
    src: matA,
    size: Object.freeze({ w: 896, h: 1200 }),
    bandY: 0.5,
    prompt: 'Top-down card game playmat, portrait 3:4: dark gunmetal riveted floor plates with engraved brass gears at the top, bottom and sides, thin crimson and gold trim lines, a horizontal gold-and-crimson divider band across the exact middle. No zone outlines, no text.',
  }),
  b: Object.freeze({
    label: 'B 真鍮の板の格子',
    src: matB,
    size: Object.freeze({ w: 896, h: 1200 }),
    bandY: 0.5,
    prompt: 'Top-down card game playmat, portrait 3:4: symmetric gunmetal panels with brass plates in the corners and center, crimson channels, a horizontal crimson divider across the exact middle. No zone outlines, no text.',
  }),
  wa: Object.freeze({
    label: 'W-A 淡い銀の鋼板',
    src: matWA,
    size: Object.freeze({ w: 896, h: 1200 }),
    bandY: 0.5,
    prompt: 'Top-down card game playmat, portrait 3:4, OVERALL LIGHT (pale / off-white) and low-contrast, horizontal divider across the exact middle, no zone markings (Gemini 2026-09-28)',
  }),
  wb: Object.freeze({
    label: 'W-B 生成りの紙と墨の歯車',
    src: matWB,
    size: Object.freeze({ w: 896, h: 1200 }),
    bandY: 0.5,
    prompt: 'Top-down card game playmat, portrait 3:4, OVERALL LIGHT (pale / off-white) and low-contrast, horizontal divider across the exact middle, no zone markings (Gemini 2026-09-28)',
  }),
  wc: Object.freeze({
    label: 'W-C 白い大理石と金の象嵌',
    src: matWC,
    size: Object.freeze({ w: 896, h: 1200 }),
    bandY: 0.5,
    prompt: 'Top-down card game playmat, portrait 3:4, OVERALL LIGHT (pale / off-white) and low-contrast, horizontal divider across the exact middle, no zone markings (Gemini 2026-09-28)',
  }),
  playmat: Object.freeze({
    label: '実写のプレイマット（枠なし）',
    src: matPlaymat,
    size: Object.freeze({ w: 1080, h: 1260 }),
    bandY: 0.5,
    prompt: '生成ではない。フィールド動画の同梱のプレイマット（client/synchro-client/src/core/field_gen/data/playmat_field.png）から破線のゾーンの枠を地の色で塗りつぶして消し（ロゴは残す）、P2 側を 180° 回して上下に並べた（2026-09-28）',
  }),
});

/**
 * ゾーンの枠（`frameMonster` / `frameSpelltrap` / `frameField` / `frameOther` の選択肢）。
 * `slice` は 9 分割の角の大きさ（絵のピクセル）。角の括弧の飾りがまるごと入る大きさ
 */
export const FRAMES = Object.freeze({
  monster: Object.freeze({ label: 'B 角の括弧（赤）', src: frameMonster }),
  spelltrap: Object.freeze({ label: 'B の色調調整版（青緑）', src: frameSpelltrap }),
  other: Object.freeze({ label: 'B の色調調整版（紫灰）', src: frameOther }),
  gold: Object.freeze({ label: 'B の色調調整版（金）', src: frameGold }),
});

/** 枠の絵の寸法と 9 分割の角（4 枚とも同じ形） */
export const FRAME_SIZE = Object.freeze({ w: 796, h: 1081 });
export const FRAME_SLICE = Object.freeze({ l: 270, t: 270, r: 270, b: 270 });

/** 枠の種類（ゾーンの種類 → 設定のキー） */
export const FRAME_KINDS = Object.freeze({
  monster: 'frameMonster',
  magic_trap: 'frameSpelltrap',
  field: 'frameField',
  maindeck: 'frameOther',
  grave: 'frameOther',
  removed: 'frameOther',
  extra: 'frameOther',
});

/** 盤の紙が描く画像（ページが先に読む。テーマの `images`） */
export const FIELD_IMAGES = Object.freeze([
  ...Object.values(MATS).map((m) => m.src),
  ...Object.values(FRAMES).map((f) => f.src),
]);
